import {
  FeatureFlag,
  FeatureFlagEnvironment,
  FeatureFlagRow,
  FeatureFlagSet,
} from '../models/FeatureFlags';
import { GoogleSheetsService } from './GoogleSheetsService';

const knownEnvironments = new Set<FeatureFlagEnvironment>(['test', 'prod']);
export const LOCAL_FEATURE_FLAG_OVERRIDES_KEY = 'ezrunpbra:localFeatureFlagOverrides';
let cachedFeatureFlags: FeatureFlagSet | null = null;

declare global {
  interface Window {
    __EZRUNPBRA_FEATURE_FLAG_ENVIRONMENT__?: FeatureFlagEnvironment;
    __EZRUNPBRA_LOCAL_FEATURE_FLAG_DEBUG__?: boolean;
  }
}

const defaultFlag = (
  key: string,
  reason: FeatureFlag['reason'],
  environment?: FeatureFlagEnvironment,
): FeatureFlag => ({
  key,
  enabled: false,
  source: 'default',
  reason,
  environment,
});

const configuredFlag = (key: string, environment?: FeatureFlagEnvironment): FeatureFlag => ({
  key,
  enabled: true,
  source: 'configured',
  reason: 'enabled',
  environment,
});

const localOverrideFlag = (key: string, enabled: boolean, environment?: FeatureFlagEnvironment): FeatureFlag => ({
  key,
  enabled,
  source: 'local',
  reason: 'local-override',
  environment,
});

export const normalizeFeatureFlagEnvironment = (environment?: string | null): FeatureFlagEnvironment | undefined => {
  const normalized = getFeatureFlagEnvironments(environment)[0];
  return normalized && knownEnvironments.has(normalized) ? normalized : undefined;
};

const getRawFeatureFlagEnvironmentValues = (environment?: string | null): string[] =>
  (environment || '')
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

const getFeatureFlagEnvironments = (environment?: string | null): FeatureFlagEnvironment[] => {
  const values = getRawFeatureFlagEnvironmentValues(environment);
  if (values.some((value) => !knownEnvironments.has(value as FeatureFlagEnvironment))) return [];
  return Array.from(new Set(values)) as FeatureFlagEnvironment[];
};

export const isLocalFeatureFlagDebugHost = (hostname?: string): boolean => {
  if (!hostname && typeof window !== 'undefined' && typeof window.__EZRUNPBRA_LOCAL_FEATURE_FLAG_DEBUG__ === 'boolean') {
    return window.__EZRUNPBRA_LOCAL_FEATURE_FLAG_DEBUG__;
  }

  const host = (hostname || (typeof window !== 'undefined' ? window.location.hostname : '')).trim().toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]';
};

export const getFeatureFlagEnvironmentForHost = (hostname?: string): FeatureFlagEnvironment | undefined => {
  if (!hostname && typeof window !== 'undefined' && window.__EZRUNPBRA_FEATURE_FLAG_ENVIRONMENT__) {
    return window.__EZRUNPBRA_FEATURE_FLAG_ENVIRONMENT__;
  }

  const host = (hostname || (typeof window !== 'undefined' ? window.location.hostname : '')).trim().toLowerCase();
  if (isLocalFeatureFlagDebugHost(host)) return 'test';
  if (host === 'test-ezrunpbra.web.app' || host.startsWith('test-')) return 'test';
  if (host === 'ezrunpbra.web.app') return 'prod';
  return undefined;
};

const readLocalFeatureFlagOverrides = (storage: Storage = window.localStorage): Record<string, boolean> => {
  try {
    const parsed = JSON.parse(storage.getItem(LOCAL_FEATURE_FLAG_OVERRIDES_KEY) || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, boolean] => typeof entry[1] === 'boolean'),
    );
  } catch {
    return {};
  }
};

export const getLocalFeatureFlagOverride = (
  key: string,
  storage: Storage = window.localStorage,
): boolean | null => {
  const overrides = readLocalFeatureFlagOverrides(storage);
  return Object.prototype.hasOwnProperty.call(overrides, key) ? overrides[key] : null;
};

export const setLocalFeatureFlagOverride = (
  key: string,
  enabled: boolean,
  storage: Storage = window.localStorage,
): boolean => {
  try {
    const overrides = readLocalFeatureFlagOverrides(storage);
    storage.setItem(LOCAL_FEATURE_FLAG_OVERRIDES_KEY, JSON.stringify({ ...overrides, [key]: enabled }));
    return true;
  } catch {
    return false;
  }
};

export const clearLocalFeatureFlagOverride = (key: string, storage: Storage = window.localStorage): boolean => {
  try {
    const overrides = readLocalFeatureFlagOverrides(storage);
    delete overrides[key];
    storage.setItem(LOCAL_FEATURE_FLAG_OVERRIDES_KEY, JSON.stringify(overrides));
    return true;
  } catch {
    return false;
  }
};

export const clearLocalFeatureFlagOverrides = (storage: Storage = window.localStorage): boolean => {
  try {
    storage.removeItem(LOCAL_FEATURE_FLAG_OVERRIDES_KEY);
    return true;
  } catch {
    return false;
  }
};

const applyLocalFeatureFlagOverrides = (
  flags: Record<string, FeatureFlag>,
  environment?: FeatureFlagEnvironment,
): Record<string, FeatureFlag> => {
  if (!isLocalFeatureFlagDebugHost()) return flags;

  const overrides = readLocalFeatureFlagOverrides();
  return Object.entries(overrides).reduce(
    (nextFlags, [key, enabled]) => ({
      ...nextFlags,
      [key]: localOverrideFlag(key, enabled, environment),
    }),
    flags,
  );
};

const isInvalidEnvironment = (row: FeatureFlagRow) =>
  Boolean(row.environment?.trim()) && getFeatureFlagEnvironments(row.environment).length === 0;

const resolveRowsForEnvironment = (
  rows: FeatureFlagRow[],
  environment?: FeatureFlagEnvironment,
): { rows: FeatureFlagRow[]; reason?: FeatureFlag['reason'] } => {
  if (!environment) {
    return { rows: [], reason: rows.some(isInvalidEnvironment) ? 'invalid' : 'environment-mismatch' };
  }

  const matchingRows = rows.filter((row) => getFeatureFlagEnvironments(row.environment).includes(environment));
  if (matchingRows.length > 0) return { rows: matchingRows };

  return { rows: [], reason: rows.some(isInvalidEnvironment) ? 'invalid' : 'environment-mismatch' };
};

export const createFeatureFlagSet = (
  flags: Record<string, FeatureFlag>,
  fallbackReason: FeatureFlag['reason'] = 'unknown',
  environment: FeatureFlagEnvironment | undefined = getFeatureFlagEnvironmentForHost(),
): FeatureFlagSet => ({
  flags,
  environment,
  isEnabled: (key: string) => flags[key]?.enabled ?? false,
  getFlag: (key: string) => flags[key] ?? defaultFlag(key, fallbackReason, environment),
});

export const resolveFeatureFlags = (
  rows: FeatureFlagRow[],
  environment: FeatureFlagEnvironment | undefined = getFeatureFlagEnvironmentForHost(),
): FeatureFlagSet => {
  const groupedRows = new Map<string, FeatureFlagRow[]>();

  rows.forEach((row) => {
    const key = row.key.trim();
    if (!key) return;
    groupedRows.set(key, [...(groupedRows.get(key) || []), row]);
  });

  const flags: Record<string, FeatureFlag> = {};
  groupedRows.forEach((featureRows, key) => {
    const selected = resolveRowsForEnvironment(featureRows, environment);
    if (selected.reason || selected.rows.length === 0) {
      flags[key] = defaultFlag(key, selected.reason || 'unknown', environment);
      return;
    }

    if (selected.rows.length > 1) {
      flags[key] = defaultFlag(key, 'duplicate', environment);
      return;
    }

    flags[key] = configuredFlag(key, environment);
  });

  return createFeatureFlagSet(applyLocalFeatureFlagOverrides(flags, environment), 'unknown', environment);
};

export const primeFeatureFlags = (
  rows: FeatureFlagRow[],
  environment: FeatureFlagEnvironment | undefined = getFeatureFlagEnvironmentForHost(),
) => {
  cachedFeatureFlags = resolveFeatureFlags(rows, environment);
};

export const getCachedFeatureFlags = (
  environment: FeatureFlagEnvironment | undefined = getFeatureFlagEnvironmentForHost(),
): FeatureFlagSet | null => {
  if (cachedFeatureFlags && cachedFeatureFlags.environment === environment) return cachedFeatureFlags;
  return null;
};

export const loadFeatureFlags = async (
  service: GoogleSheetsService = new GoogleSheetsService(),
  environment: FeatureFlagEnvironment | undefined = getFeatureFlagEnvironmentForHost(),
): Promise<FeatureFlagSet> => {
  try {
    cachedFeatureFlags = resolveFeatureFlags(await service.getFeatureFlagRows(), environment);
    return cachedFeatureFlags;
  } catch {
    cachedFeatureFlags = createFeatureFlagSet(applyLocalFeatureFlagOverrides({}, environment), 'unavailable', environment);
    return cachedFeatureFlags;
  }
};

export const isFeatureEnabled = async (
  key: string,
  service: GoogleSheetsService = new GoogleSheetsService(),
  environment: FeatureFlagEnvironment | undefined = getFeatureFlagEnvironmentForHost(),
): Promise<boolean> => {
  const flagSet = await loadFeatureFlags(service, environment);
  return flagSet.isEnabled(key);
};

export const getFeatureFlag = async (
  key: string,
  service: GoogleSheetsService = new GoogleSheetsService(),
  environment: FeatureFlagEnvironment | undefined = getFeatureFlagEnvironmentForHost(),
): Promise<FeatureFlag> => {
  const flagSet = await loadFeatureFlags(service, environment);
  return flagSet.getFlag(key);
};
